import React, { useState, useEffect } from 'react';
import { Store as StoreIcon, Package } from 'lucide-react';
import { ResponsiveImage } from './ResponsiveImage';
import { getEffectiveUnitPrice, getSalePercent } from '../utils/pricing';

/**
 * ProductDetailModal component
 * Props:
 *  - product: product object to display (required)
 *  - cart: array of cart items for determining quantity
 *  - onAddToCart(product)
 *  - onRemoveFromCart(productId)
 *  - onClose()
 */
export const ProductDetailModal = ({ product, cart, onAddToCart, onRemoveFromCart, onClose, onContinueToCart }) => {
    if (!product) return null;

    // State for selected paid options
    const [selectedPaidOptions, setSelectedPaidOptions] = useState([]);

    // Reset selected options when product changes
    useEffect(() => {
        setSelectedPaidOptions([]);
    }, [product.product_id]);

    // Check if any variation of this product is in cart (for badge display)
    const totalQuantityInCart = cart?.filter(i => i.product_id === product.product_id)
        .reduce((sum, item) => sum + item.quantity, 0) || 0;
    // Effective stock = min(product-level, category-level) when each is enabled
    const productInvEnabled = product.inventory?.enabled;
    const categoryInvEnabled = product.category_inventory?.enabled;
    const parseStock = (v) => {
        if (v === undefined || v === null || v === '') return undefined;
        const n = parseInt(v, 10);
        return isNaN(n) ? undefined : n;
    };
    const productStock = productInvEnabled ? parseStock(product.inventory?.quantity ?? product.stock_quantity)
                                            : parseStock(product.stock_quantity);
    const categoryStock = categoryInvEnabled ? parseStock(product.category_inventory?.quantity) : undefined;
    const limits = [productStock, categoryStock].filter((n) => typeof n === 'number');
    const stock = limits.length > 0 ? Math.min(...limits) : undefined;
    const inventoryEnabled = productInvEnabled || categoryInvEnabled;
    const isOutOfStock = !!product.sold_out || (inventoryEnabled && typeof stock === 'number' && stock <= 0);

    // Toggle a paid option
    const togglePaidOption = (option) => {
        setSelectedPaidOptions(prev => {
            const exists = prev.some(opt => opt.name === option.name);
            if (exists) {
                return prev.filter(opt => opt.name !== option.name);
            } else {
                return [...prev, option];
            }
        });
    };

    // Calculate total price including selected options
    const calculateTotalPrice = () => {
        const basePrice = parseFloat(product.price) || 0;
        const optionsPrice = selectedPaidOptions.reduce((sum, opt) => sum + (parseFloat(opt.price) || 0), 0);
        return basePrice + optionsPrice;
    };

    const totalPrice = calculateTotalPrice();

    // Helper to add product with selected options
    const handleAddToCart = () => {
        const productWithOptions = {
            ...product,
            selectedPaidOptions: selectedPaidOptions
        };
        onAddToCart(productWithOptions);
    };

    // Footer action area extracted for fixed positioning
    const renderActions = () => {
        if (isOutOfStock) {
            return (
                <div className="flex items-center gap-4 bg-danger-50 border border-danger-100 rounded-xl p-3 w-full text-danger-600 font-bold justify-center">
                    אזל המלאי
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-3 w-full">
                <button
                    onClick={() => {
                        handleAddToCart();
                        // Auto-open cart after adding to show the item was added
                        if (onContinueToCart) {
                            setTimeout(() => onContinueToCart(), 300);
                        }
                    }}
                    className="bg-gradient-to-r from-mekomit-500 to-mekomit-400 hover:from-mekomit-600 hover:to-mekomit-500 text-white font-bold py-4 px-6 rounded-xl transition-all shadow w-full"
                >
                    הוסף לסל - ₪{totalPrice.toFixed(2)}
                </button>
                {totalQuantityInCart > 0 && (
                    <div className="text-center text-xs text-mekomit-600">
                        {totalQuantityInCart} פריטים מסוג זה כבר בסל
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fadein"
            onClick={onClose}
        >
            <div
                className="relative bg-white rounded-3xl w-full max-w-2xl sm:max-w-3xl shadow-2xl flex flex-col max-h-[90vh] animate-slide-up"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header (fixed at top of modal) */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-mekomit-100 bg-gradient-to-r from-mekomit-500 to-mekomit-400 shrink-0">
                    <h2 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-3">
                        <StoreIcon className="w-6 h-6" /> {product.name}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                        aria-label="סגור"
                    >
                        ✕
                    </button>
                </div>

                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto">
                    <div className="grid md:grid-cols-2 gap-6 p-4 sm:p-6">
                        {/* Images */}
                        <div className="space-y-3">
                            {product.images && product.images.length > 0 ? (
                                <div className="flex flex-col gap-3">
                                    <div className="relative rounded-2xl border border-mekomit-100 shadow overflow-hidden">
                                        <ResponsiveImage
                                            src={product.images[0]}
                                            alt={product.name}
                                            aspectRatio="aspect-square"
                                            lazy={true}
                                            placeholderClassName="bg-gradient-to-br from-mekomit-50 to-mekomit-100"
                                        />
                                        {product.sale?.enabled && product.sale?.percent > 0 && (
                                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-r from-danger-600 to-danger-500 text-white text-center py-1.5 px-3 shadow-md">
                                                <span className="text-sm font-bold tracking-wide">
                                                    במבצע · {product.sale.percent}% הנחה
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {product.images.length > 1 && (
                                        <div className="grid grid-cols-4 gap-2">
                                            {product.images.slice(1, 5).map((img, idx) => (
                                                <div key={idx} className="rounded-lg border border-mekomit-100 overflow-hidden">
                                                    <ResponsiveImage
                                                        src={img}
                                                        alt={product.name + ' ' + (idx + 2)}
                                                        aspectRatio="aspect-square"
                                                        lazy={true}
                                                        placeholderClassName="bg-gradient-to-br from-mekomit-50 to-mekomit-100"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="w-full aspect-square rounded-2xl bg-mekomit-100 flex items-center justify-center border border-mekomit-200">
                                    <Package className="w-16 h-16 text-mekomit-300" />
                                </div>
                            )}
                        </div>

                        {/* Details */}
                        <div className="flex flex-col gap-4">
                            <div>
                                <h3 className="text-lg font-bold text-mekomit-900 mb-2">תיאור</h3>
                                <p className="text-mekomit-800 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                                    {product.description || 'אין תיאור זמין למוצר זה.'}
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="bg-mekomit-50 border border-mekomit-100 rounded-xl px-4 py-3 flex flex-col">
                                    <span className="text-xs text-mekomit-700">מחיר</span>
                                    {getSalePercent(product) > 0 ? (
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-2xl font-bold text-danger-600">₪{getEffectiveUnitPrice(product).toFixed(2)}</span>
                                            <span className="text-sm line-through text-mekomit-500">₪{product.price}</span>
                                        </div>
                                    ) : (
                                        <span className="text-2xl font-bold text-mekomit-900">₪{product.price}</span>
                                    )}
                                </div>
                                {/* {stock !== undefined && (
                                    <div className="bg-mekomit-50 border border-mekomit-100 rounded-xl px-4 py-3 flex flex-col">
                                        <span className="text-xs text-mekomit-700">מלאי</span>
                                        <span className="text-xl font-bold text-mekomit-900">{stock}</span>
                                    </div>
                                )} */}
                            </div>
                            {product.tags && product.tags.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-bold text-mekomit-700 mb-2">תגיות</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {product.tags.map(tag => (
                                            <span key={tag} className="text-xs bg-mekomit-100 text-mekomit-700 px-2 py-1 rounded-full border border-mekomit-200">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Paid Options Section */}
                            {product.paid_options && product.paid_options.length > 0 && (
                                <div className="border-t border-mekomit-100 pt-4">
                                    <h4 className="text-sm font-bold text-mekomit-700 mb-3">תוספות בתשלום</h4>
                                    <div className="space-y-2">
                                        {product.paid_options.map((option, idx) => {
                                            const isSelected = selectedPaidOptions.some(opt => opt.name === option.name);
                                            return (
                                                <button
                                                    key={idx}
                                                    onClick={() => togglePaidOption(option)}
                                                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                                                        isSelected
                                                            ? 'border-mekomit-500 bg-mekomit-50'
                                                            : 'border-mekomit-200 bg-white hover:border-mekomit-300 hover:bg-mekomit-25'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                                            isSelected
                                                                ? 'border-mekomit-500 bg-mekomit-500'
                                                                : 'border-mekomit-300'
                                                        }`}>
                                                            {isSelected && (
                                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <span className="text-sm font-medium text-mekomit-900">{option.name}</span>
                                                    </div>
                                                    <span className="text-sm font-bold text-mekomit-700">+₪{parseFloat(option.price).toFixed(2)}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {selectedPaidOptions.length > 0 && (
                                        <div className="mt-3 p-3 bg-mekomit-50 border border-mekomit-200 rounded-lg">
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="font-medium text-mekomit-700">סה"כ תוספות:</span>
                                                <span className="font-bold text-mekomit-900">
                                                    +₪{selectedPaidOptions.reduce((sum, opt) => sum + parseFloat(opt.price), 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer (fixed action buttons) */}
                <div className="px-4 sm:px-6 py-4 border-t border-mekomit-100 bg-white shadow-inner sticky bottom-0">
                    {renderActions()}
                </div>
            </div>
        </div>
    );
};

export default ProductDetailModal;
